import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { baseUrl } from '../../constant/config';
import { PLATFORM_META, formatDate } from './shareMeta';

const truncate = (text, max = 160) =>
  !text ? '' : text.length > max ? `${text.slice(0, max)}...` : text;

/**
 * A preview of the post being deleted, so the confirmation is about a specific
 * post rather than an abstract "are you sure".
 */
function PostPreview({ target, platform }) {
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ p: 1.5, mb: 2, borderRadius: 1, bgcolor: 'grey.100', alignItems: 'flex-start' }}
    >
      {target.thumbnail ? (
        <Box
          component="img"
          src={target.thumbnail}
          alt=""
          sx={{ width: 72, height: 72, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }}
        />
      ) : (
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: 1,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.300',
            color: 'text.disabled',
          }}
        >
          <MovieOutlinedIcon />
        </Box>
      )}

      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
          {platform.icon}
          <Typography variant="subtitle2">{platform.label}</Typography>
          {target.mediaType && <Chip size="small" label={target.mediaType} variant="outlined" />}
          {target.fromDebate && target.title && (
            <Chip size="small" color="primary" variant="outlined" label="from a debate" />
          )}
        </Stack>

        {target.title && (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {target.title}
          </Typography>
        )}

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {truncate(target.caption || target.story) || 'No caption'}
        </Typography>

        <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }} flexWrap="wrap">
          {target.createdTime && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {formatDate(target.createdTime)}
            </Typography>
          )}
          {typeof target.likeCount === 'number' && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {target.likeCount} likes
            </Typography>
          )}
          {typeof target.commentCount === 'number' && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {target.commentCount} comments
            </Typography>
          )}
          {target.permalink && (
            <Link
              variant="caption"
              href={target.permalink}
              target="_blank"
              rel="noreferrer"
              underline="hover"
            >
              View post
            </Link>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}

PostPreview.propTypes = {
  target: PropTypes.object.isRequired,
  platform: PropTypes.object.isRequired,
};

/**
 * Confirm deleting a post, or dropping a row from the publish history.
 *
 * Taking a post off Facebook or Instagram is public and cannot be undone, so
 * that choice has to be acknowledged explicitly before the button becomes
 * usable. Removing a local history row changes nothing outside this app, so it
 * is confirmed the ordinary way.
 */
export default function DeleteShareDialog({ open, onClose, target, onDone }) {
  const queryClient = useQueryClient();
  const platform = PLATFORM_META[target?.platform] || {};
  const isRecord = target?.mode === 'record';

  // A history row with no post id never reached the platform, and one already
  // marked deleted has nothing left to take down - either way the only thing
  // that can be removed is the record itself.
  const recordOnly = isRecord && (!target?.postId || target?.status !== 'published');
  const [scope, setScope] = useState('platform');
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (!open) return;
    setScope(recordOnly ? 'record' : 'platform');
    setAcknowledged(false);
  }, [open, recordOnly]);

  // Deleting on Meta is the irreversible, public one.
  const destructive = !isRecord || scope === 'platform';

  // Switching between the two choices must not carry an acknowledgement over.
  useEffect(() => {
    setAcknowledged(false);
  }, [scope]);

  const {
    mutate: remove,
    isLoading: removing,
    isError,
    error,
    reset,
  } = useMutation(
    async () => {
      const { data } = isRecord
        ? await axios.delete(`${baseUrl}/api/videos/shares/${target.shareId}`, {
            params: { scope },
          })
        : await axios.delete(
            `${baseUrl}/api/videos/platform-posts/${encodeURIComponent(target.postId)}`,
            { params: { platform: target.platform } }
          );
      return data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('platformPosts');
        queryClient.invalidateQueries('platformShares');
        queryClient.invalidateQueries('recordings');
        onDone?.(data.message || 'Deleted');
        onClose();
      },
    }
  );

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const blocked = destructive && !acknowledged;

  return (
    <Dialog open={open} onClose={removing ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <WarningAmberIcon color={destructive ? 'error' : 'warning'} />
          <span>
            {destructive ? `Delete this ${platform.label} post?` : 'Remove this history row?'}
          </span>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {target && <PostPreview target={target} platform={platform} />}

        {isRecord && recordOnly && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {target?.status === 'deleted'
              ? 'This post has already been deleted from the platform. Only its history record is left.'
              : 'This attempt never produced a post, so there is nothing live to take down.'}
          </Alert>
        )}

        {isRecord && !recordOnly && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              What should be deleted?
            </Typography>
            <RadioGroup value={scope} onChange={(e) => setScope(e.target.value)}>
              <FormControlLabel
                value="platform"
                disabled={removing}
                control={<Radio />}
                label={
                  <>
                    <Typography variant="body2">Delete the post from {platform.label}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Removes it for everyone. The history row is kept and marked deleted.
                    </Typography>
                  </>
                }
              />
              <FormControlLabel
                value="record"
                disabled={removing}
                sx={{ mt: 1 }}
                control={<Radio />}
                label={
                  <>
                    <Typography variant="body2">Remove the history row only</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      The post stays live on {platform.label}.
                    </Typography>
                  </>
                }
              />
            </RadioGroup>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {destructive ? (
          <>
            <Alert severity="error" icon={<WarningAmberIcon />}>
              This permanently removes the post from {platform.label} for everyone who can see it.
              It cannot be undone, and publishing again creates a new post with a new link.
            </Alert>
            <FormControlLabel
              sx={{ mt: 1 }}
              disabled={removing}
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  I understand this post will be deleted from {platform.label} permanently.
                </Typography>
              }
            />
          </>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            This only removes the row from this app&apos;s publish history. Nothing on{' '}
            {platform.label} changes.
          </Typography>
        )}

        {isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error?.response?.data?.message || error?.message}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={removing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => remove()}
          disabled={removing || blocked}
          startIcon={removing ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {removing
            ? 'Deleting...'
            : destructive
            ? `Delete from ${platform.label}`
            : 'Remove record'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DeleteShareDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  target: PropTypes.object,
  onDone: PropTypes.func,
};
